// Ruta: packages/ui/src/feedback/ErrorBoundary.tsx
// React Error Boundary — catches rendering errors and shows a user-friendly screen in Spanish.
// Usage: wrap <App /> or any subtree.
//
// HAL-FE-003

import React, { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { cn, telemetry } from '@heyshaker/shared'
import { Button } from '../components/Button.js'

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional custom fallback component. Receives error + reset callback. */
  fallback?: (props: { error: Error; resetErrorBoundary: () => void }) => ReactNode
  /** Called when the boundary catches an error (e.g. for logging). */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
  className?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.props.onError?.(error, errorInfo)

    // Report to telemetry service (HAL-FE-002)
    telemetry.trackError(error, {
      source: 'ErrorBoundary',
      componentStack: errorInfo.componentStack?.slice(0, 2000),
    })

    if (import.meta.env.DEV) {
      console.error('[ErrorBoundary]', error, errorInfo)
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Custom fallback takes precedence
      if (this.props.fallback) {
        return this.props.fallback({
          error: this.state.error,
          resetErrorBoundary: this.resetErrorBoundary,
        })
      }

      // Default fallback UI — Spanish, consistent with HeyShaker design system
      return (
        <div
          className={cn(
            'flex min-h-screen items-center justify-center bg-background p-6',
            this.props.className,
          )}
        >
          <div className="flex max-w-md flex-col items-center gap-6 text-center" role="alert">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
            </div>

            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-foreground">
                Algo salió mal
              </h1>
              <p className="text-sm text-muted-foreground">
                Ocurrió un error inesperado en la aplicación. Por favor intenta de nuevo.
                Si el problema persiste, contacta al equipo de soporte.
              </p>
              {import.meta.env.DEV && this.state.error && (
                <details className="mt-4 rounded-md border border-border bg-muted p-3 text-left">
                  <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
                    Detalle del error (solo desarrollo)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs text-destructive">
                    {this.state.error.message}
                    {'\n'}
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </div>

            <Button variant="outline" size="sm" onClick={this.resetErrorBoundary}>
              <RefreshCw className="h-4 w-4" />
              Reintentar
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
